import { useForm } from "react-hook-form";
import type { MutateOptions } from "react-query";
import type { Collective, CollectiveFormFields } from "../types";
import { useCreateCollective } from "./useCreateCollective";
import { useUpdateCollective } from "./useUpdateCollective";

type UseCollectiveFormParams = {
  collective?: Collective;
} & MutateOptions<Collective, unknown, CollectiveFormFields>;

const useCollectiveForm = ({
  collective,
  ...options
}: UseCollectiveFormParams) => {
  // Transform collective into default values, if necessary
  const { handleSubmit, ...form } = useForm<CollectiveFormFields>({
    defaultValues: collective ?? { affiliatedArtists: [] },
  });

  const createMutation = useCreateCollective();
  const updateMutation = useUpdateCollective();

  const isEdit = !!collective;
  const mutation = isEdit ? updateMutation : createMutation;

  const onSubmit = handleSubmit(({ banner, links, ...formData }) => {
    // Transform required field values into request data, if necessary
    const transformed = {
      ...formData,
      // Banner images are optional, so set undefined if URL is an empty string
      banner: banner?.url === "" ? undefined : banner,
      // Links are all optional, so filter out empty strings
      links: links.filter((l) => l.url),
    };

    mutation.mutate(
      isEdit ? { ...collective, ...transformed } : transformed,
      options
    );
  });

  return {
    ...form,
    isEdit,
    mutation,
    onSubmit,
  };
};

export { useCollectiveForm };
